import {isElement} from '@mantine/utils';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import * as browser from 'lib/Browser';
import FormatState from 'notebook/mediocre/formatted-text/FormatState';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import indexOfRegExp from 'util/indexOfRegExp';

interface Selected {
  index?: number;
  subIndex?: string;
  editorKey?: string;
  hasNonEmptyTextSelection?: boolean;
}

export interface SignState {
  query: {text: string; start: number; end: number};
  box: {top: number; left: number; bottom: number};
  hidden: boolean;
  index: number;
}

export interface HyperlinkState {
  box: {top: number; left: number; bottom: number; distanceFromViewportBottom: number};
  link: {text: string; url: string};
}

export const HYPERLINK_STATE_SHAPE = PropTypes.shape({
  box: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    distanceFromViewportBottom: PropTypes.number.isRequired,
  }).isRequired,
});

interface SelectionState {
  box: {top: number; left: number; bottom: number};
}

interface SelectionReaderProps {
  selected: Selected;
  onChangeSelected: (selected: Selected) => void;
  formatState: {[format: string]: boolean | string | null};
  onChangeFormatState: (formatState: {[format: string]: boolean | string | null}) => void;
  atSignState: SignState;
  onChangeAtSignState: (atSignState: SignState | null) => void;
  hyperlinkState: HyperlinkState;
  onChangeHyperlinkState: (hyperlinkState: HyperlinkState | null) => void;
  scrollableParentSelector: string;
  processIndex: (editorKey: string, index: string) => number;
  slashSignState: SignState;
  onChangeSlashSignState: (slashSignState: SignState | null) => void;
  nonEmptySelectionState: SelectionState;
  onChangeNonEmptySelectionState: (nonEmptySelectionState: SelectionState | null) => void;
  /**
   * N.B.: `children` must be a single child element, not an array, not a fragment, that forwards or natively
   * accepts a ref.
   */
  children: React.ReactElement;
}

class SelectionReader extends React.Component<SelectionReaderProps> {
  ref = React.createRef<HTMLElement>();

  static propTypes = {
    selected: PropTypes.object,
    onChangeSelected: PropTypes.func.isRequired,
    formatState: PropTypes.object,
    onChangeFormatState: PropTypes.func.isRequired,
    atSignState: PropTypes.object,
    onChangeAtSignState: PropTypes.func.isRequired,
    hyperlinkState: PropTypes.object,
    onChangeHyperlinkState: PropTypes.func.isRequired,
    scrollableParentSelector: PropTypes.string.isRequired,
    // Reads the index from the data-index attribute of the DOM and then processes it into its normal form.
    processIndex: PropTypes.func.isRequired,
    slashSignState: PropTypes.object,
    onChangeSlashSignState: PropTypes.func.isRequired,
    nonEmptySelectionState: PropTypes.object,
    onChangeNonEmptySelectionState: PropTypes.func.isRequired,
  };

  static defaultProps = {
    processIndex(editorKey: string, index: string): number {
      return parseInt(index, 10);
    },
  };

  _isMounted = false;

  _pollTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  _pollRafHandle: number | null = null;

  override componentDidMount(): void {
    this._isMounted = true;
    if (browser.isBrowser) {
      this.updateFromRange();
    }
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
    // Cancel the pending self-rescheduling poll so it can't fire after the component is unmounted (which in
    // the shared-process RTL suite could otherwise mutate selection state during a later test).
    if (this._pollTimeoutHandle != null) {
      clearTimeout(this._pollTimeoutHandle);
    }
    if (this._pollRafHandle != null) {
      cancelAnimationFrame(this._pollRafHandle);
    }
  }

  setSelected(selected: Selected): void {
    if (!isEqual(this.props.selected, selected)) {
      this.props.onChangeSelected(selected);
    }
  }

  setFormats(formats: {[format: string]: boolean | string | null}): void {
    if (!isEqual(this.props.formatState, formats)) {
      this.props.onChangeFormatState(formats);
    }
  }

  _calculateBox(
    startContainer: Node,
    startIndex: number,
    calculateFromTop?: boolean
  ): {
    top: number;
    left: number;
    bottom: number;
    distanceFromViewportBottom: number;
  } {
    // Measure the rendered position of the startContainer at startIndex
    if (calculateFromTop == null) {
      calculateFromTop = true;
    }
    const range = document.createRange();
    range.setStart(startContainer, startIndex);
    range.setEnd(startContainer, startIndex + 1);
    const rect = range.getBoundingClientRect();
    let {top, left} = rect;
    const {bottom, height} = rect;
    // ...relative to the specified parent.
    const $scrollableContainer = $(this.ref.current!).closest(this.props.scrollableParentSelector);
    const noteEditorOffset = $scrollableContainer.offset();
    const topAnchor = calculateFromTop ? top : bottom;
    top = topAnchor - noteEditorOffset!.top + $scrollableContainer.scrollTop();
    left = left - noteEditorOffset!.left + $scrollableContainer.scrollLeft();
    return {
      top,
      left,
      bottom: top + height,
      distanceFromViewportBottom:
        $scrollableContainer.innerHeight() - (topAnchor - noteEditorOffset!.top - height),
    };
  }

  readSlashSignState(range: Range, index: number, div: HTMLElement): SignState | null {
    if (!$(div).is('[data-allow-slash-search]')) {
      return null;
    }

    let prevSlashSignState: SignState | null = this.props.slashSignState;
    // if previous slash sign is somewhere else, we start fresh
    if (prevSlashSignState?.index !== index) {
      prevSlashSignState = null;
    }

    // no-op if the range spans multiple nodes being used
    if (range.startContainer !== range.endContainer) {
      return null;
    }

    const text = range.startContainer.textContent!;
    // If there is no /-sign before the cursor, abort.
    if (range.startOffset === 0) {
      return null;
    }

    const lastSlashIndex = text.lastIndexOf('/', range.startOffset - 1);
    if (lastSlashIndex === -1) {
      return null;
    }

    // If we aren't at the start of the line, or the /-sign isn't preceded by whitespace, abort:
    // user may be typing a "date". (/<cursor><whitespace>)
    if (lastSlashIndex > 0 && !/\s/.test(text.charAt(lastSlashIndex - 1))) {
      return null;
    }

    // If there's whitespace closer to the cursor than the /-sign AND we don't already have the dropdown open,
    // abort (last typed / is no longer relevant)
    if (prevSlashSignState == null) {
      const nextSpaceIndex = indexOfRegExp('\\s', text, lastSlashIndex);
      if (nextSpaceIndex !== -1 && nextSpaceIndex <= range.startOffset - 1) {
        return null;
      }
    }

    // The query extends until the first whitespace after the cursor, or the end of the text.
    let endOfQueryIndex = indexOfRegExp('\\s', text, range.startOffset - 1);
    if (endOfQueryIndex === -1) {
      endOfQueryIndex = text.length;
    }

    const queryText = text.slice(lastSlashIndex + 1, endOfQueryIndex);
    // Abort if we start with a space character ' ' (e.g. '/<whitespace><cursor>')
    if (queryText.length === 0 && lastSlashIndex !== range.startOffset - 1) {
      return null;
    }

    // Only start a query if we've typed `/<cursor>`
    if (prevSlashSignState == null && queryText.length !== 0) {
      return null;
    }

    const box = this._calculateBox(range.startContainer, lastSlashIndex);

    // Translate the indices to account for indices relative to the full-text of the parent div.
    // Indices will be used for formatting - therefore do no include text in non-editable components
    const indexOffset = selutil.rangeOffset(range.startContainer, 0, div, true);
    const query = {
      text: queryText,
      start: indexOffset + lastSlashIndex,
      end: indexOffset + endOfQueryIndex,
    };

    const hidden =
      prevSlashSignState == null
        ? false
        : prevSlashSignState.hidden && prevSlashSignState.query.start === query.start;
    return {query, box, hidden, index};
  }

  readAtSignState(range: Range, index: number, div: HTMLElement): SignState | null {
    if (!$(div).is('[data-allow-at-search]')) {
      return null;
    }

    let prevAtSignState: SignState | null = this.props.atSignState;
    if ((prevAtSignState != null ? prevAtSignState.index : undefined) !== index) {
      prevAtSignState = null;
    }

    if (range.startContainer !== range.endContainer) {
      return null;
    }

    const text = range.startContainer.textContent!;
    // If there is no @-sign before the cursor, abort.
    if (range.startOffset === 0) {
      return null;
    }
    const lastAtIndex = text.lastIndexOf('@', range.startOffset - 1);
    if (lastAtIndex === -1) {
      return null;
    }

    // If we aren't at the start of the line, or the @-sign isn't preceded by whitespace or an opening
    // bracket, abort: user may be typing an email address. (- 1 to avoid erroneous aborting for '@| ')
    // eslint-disable-next-line no-useless-escape
    if (lastAtIndex > 0 && !/[\s(\[{]/.test(text.charAt(lastAtIndex - 1))) {
      return null;
    }

    // If there's whitespace closer to the cursor than the @-sign AND we don't already have the dropdown open,
    // abort (last typed @ is no longer relevant)
    if (prevAtSignState == null) {
      const nextSpaceIndex = indexOfRegExp('\\s', text, lastAtIndex);
      if (nextSpaceIndex !== -1 && nextSpaceIndex <= range.startOffset - 1) {
        return null;
      }
    }

    // The query extends until the first whitespace or closing bracket after the cursor, or the end of the
    // text.
    let endOfQueryIndex = indexOfRegExp('[\\s)\\]}]', text, range.startOffset - 1);
    if (endOfQueryIndex === -1) {
      endOfQueryIndex = text.length;
    }
    const queryText = text.slice(lastAtIndex + 1, endOfQueryIndex);
    // Abort if we start with a space character ' ' (e.g. '@ |')
    if (queryText.length === 0 && lastAtIndex !== range.startOffset - 1) {
      return null;
    }

    // Only start a query if we've typed `@|`
    if (prevAtSignState == null && queryText.length !== 0) {
      return null;
    }

    const box = this._calculateBox(range.startContainer, lastAtIndex);

    // Translate the indices to account for indices relative to the full-text of the parent div.
    // Indices will be used for formatting - therefore do no include text in non-editable components
    const indexOffset = selutil.rangeOffset(range.startContainer, 0, div, true);
    const query = {
      text: queryText,
      start: indexOffset + lastAtIndex,
      end: indexOffset + endOfQueryIndex,
    };
    const hidden =
      prevAtSignState == null ? false : prevAtSignState.hidden && prevAtSignState.query.start === query.start;
    return {query, box, hidden, index};
  }

  setAtSignState(newAtSignState: SignState | null): void {
    if (!isEqual(this.props.atSignState, newAtSignState)) {
      this.props.onChangeAtSignState(newAtSignState);
    }
  }

  setSlashSignState(newSlashSignState: SignState | null): void {
    if (!isEqual(this.props.slashSignState, newSlashSignState)) {
      this.props.onChangeSlashSignState(newSlashSignState);
    }
  }

  readHyperlinkState(range: Range, index: number, div: HTMLElement): HyperlinkState | null {
    // Query if there is an anchor node ancestor.
    let node;
    if (selutil.closest(div, '.internalLink')) {
      return null;
    }
    let anchorNode = selutil.closest(div, 'a');
    if (anchorNode != null) {
      // If we're at the end of the hyperlink, abort. This is consistent with clicking the link format button.
      if (selutil.selectionIsAtEnd({target: anchorNode})) {
        return null;
      }
    } else {
      node = range.startContainer;
      // If we're at the end of a non-anchor node but the next node is an anchor (i.e. at the start of an
      // anchor node), use the next node as the anchor node.
      if (
        (node.nextSibling != null ? node.nextSibling.nodeName : undefined) === 'A' &&
        selutil.selectionIsAtEnd({target: node})
      ) {
        anchorNode = node.nextSibling as Element;
      } else {
        return null;
      }
    }
    const linkAnchor = anchorNode as HTMLAnchorElement;
    const href = linkAnchor.getAttribute('href');
    if (!href) {
      return null;
    }
    const link = {
      url: href,
      text: linkAnchor.textContent,
    };
    const box = this._calculateBox(linkAnchor, 0, true);

    return {box, link};
  }

  setHyperlinkState(newHyperlinkState: HyperlinkState | null): void {
    if (!isEqual(this.props.hyperlinkState, newHyperlinkState)) {
      this.props.onChangeHyperlinkState(newHyperlinkState);
    }
  }

  // TODO (BNCH-72406): cleanup, any other cases to consider?
  readNonEmptySelectionState(range: Range, index: number, div: HTMLElement): SelectionState | null {
    // If there is an empty selection or no text is selected, abort
    if (range.collapsed || range.startContainer.textContent == null) {
      return null;
    }
    // Position floating box underneath the selected text aligned to the start of selection
    const startIndex = range.startOffset > 0 ? range.startOffset - 1 : 0;
    const box = this._calculateBox(range.startContainer, startIndex, false);

    return {box};
  }

  setNonEmptySelectionState(newNonEmptySelectionState: SelectionState | null): void {
    if (!isEqual(this.props.nonEmptySelectionState, newNonEmptySelectionState)) {
      this.props.onChangeNonEmptySelectionState(newNonEmptySelectionState);
    }
  }

  updateFromRange = (): void => {
    let $div;
    let enabled;
    if (!this._isMounted) {
      return;
    }

    this._pollTimeoutHandle = setTimeout(() => {
      this._pollRafHandle = requestAnimationFrame(this.updateFromRange);
    }, 250);

    const range = selutil.getRange();
    const rangeInDOMNode = range != null && $(range.startContainer).closest(this.ref.current!).length > 0;
    if (rangeInDOMNode) {
      $div = $(range.startContainer).closest('[data-index][data-formattable]');
      enabled = $div.length > 0;
    } else {
      enabled = false;
    }

    if (!enabled) {
      this.setSelected({});
      this.setFormats(FormatState.defaultFormats());
      this.setAtSignState(null);
      this.setSlashSignState(null);
      if (BenchlingConfig.ENABLE_HOVERING_FORMAT_TOOLBAR) {
        this.setNonEmptySelectionState(null);
      }
      this.setHyperlinkState(null);
      return;
    }

    const $editor = ($div as JQuery).parents('[data-editor]');
    const editorKey = $editor.data('editor');
    const index = this.props.processIndex(editorKey, ($div as JQuery).attr('data-index'));
    const subIndex = ($div as JQuery).attr('data-subindex');
    const hasNonEmptyTextSelection = !range!.collapsed;
    const selection = {index, subIndex, editorKey, hasNonEmptyTextSelection};
    this.setAtSignState(this.readAtSignState(range!, index, ($div as JQuery)[0]));
    this.setSlashSignState(this.readSlashSignState(range!, index, ($div as JQuery)[0]));
    if (BenchlingConfig.ENABLE_HOVERING_FORMAT_TOOLBAR) {
      this.setNonEmptySelectionState(this.readNonEmptySelectionState(range!, index, ($div as JQuery)[0]));
    }
    this.setHyperlinkState(this.readHyperlinkState(range!, index, ($div as JQuery)[0]));
    this.setSelected(selection);
    this.setFormats(FormatState.readFormats());
  };

  override render(): React.ReactNode {
    if (!isElement(this.props.children)) {
      throw new Error('SelectionReader requires a single child element');
    }
    return React.cloneElement(this.props.children, {ref: this.ref});
  }
}

export default SelectionReader;
